/**
 * Online quote capture form — /quote/online. Shown under the instant
 * estimate; replaces the old "email my results" form (name + phone + email).
 *
 * The builder's inputs and the quote breakdown arrive as `context`, built by
 * onlineQuoteContext below. The builder starts pre-filled, so a value the
 * visitor never changed is labelled "(default)" in the Note and is not written
 * to CRM fields — only figures the visitor actually entered are.
 */
import { CARD_BANDS } from "@/utils/pricingEngine";
import { company, email, fullName, phoneRequired } from "./common";

/** Legacy analytics name kept so quote_requested / email_capture reports continue. */

const K = {
  turnover: "Monthly online card turnover (£)",
  atv: "Average order value (£)",
  monthly: "Estimated monthly cost",
};

const gbp = (n) =>
  `£${Number(n).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const num = (v) => (typeof v === "number" && v > 0 ? v : null);

/**
 * `inputs` are the builder's raw strings, `initial` its starting values.
 * Unchanged pre-filled values become "<value> (default)" strings, which
 * contextToCrm ignores (it only takes numbers).
 */
export function onlineQuoteContext(inputs, results, initial) {
  if (!results) return {};
  const value = (key) => {
    const n = parseFloat(inputs[key]);
    if (!(n > 0)) return null;
    return String(inputs[key]) === String(initial[key]) ? `${n} (default)` : n;
  };
  const custom = inputs.cardMixMode === "advanced";
  return {
    [K.turnover]: value("monthlyTurnover"),
    [K.atv]: value("averageTransactionValue"),
    "Card mix": custom
      ? `Custom: ${CARD_BANDS.map((b) => `${b.label} ${parseFloat(inputs[`mix_${b.key}`]) || 0}%`).join(", ")}`
      : "Standard",
    "Processing & gateway fees": `${gbp(results.processingCost)}/month`,
    "PCI compliance": `${gbp(results.pciCost)}/month`,
    "Monthly transactions": Math.round(results.transactionCount),
    [K.monthly]: gbp(results.estimatedMonthlyCost),
    "Estimated annual cost": gbp(results.estimatedAnnualCost),
  };
}

const quoteOnline = {
  id: "quote_online",
  label: "Online Quote",
  crm: { layout: "Standard", leadSource: "Website Quote Builder" },
  fields: [
    [fullName, company],
    [email, { ...phoneRequired, help: "We'll only call to discuss your quote — never spam." }],
  ],
  submitLabel: "Request my tailored quote",
  consent: { variant: "standard" },
  success: {
    title: "Thank you",
    body: "We'll prepare a personalised quote for your online payment setup and be in touch shortly.",
  },
  contextToCrm: (ctx) => ({
    Monthly_Card_Turnovers: num(ctx[K.turnover]),
    ATV: num(ctx[K.atv]),
    Needs: ["Online payments"],
    How_do_you_take_payments_now: ["Online payments"],
  }),
  summary: (values, ctx) =>
    ctx[K.monthly] ? `Online quote: estimate ${ctx[K.monthly]} a month` : "Online quote",
};

export default quoteOnline;
